import {
  Building2,
  FileChartColumn,
  FileChartLine,
  FileText,
  Handshake,
  Landmark,
  MapPinned,
  Paperclip,
  Text,
  TextSearch,
  User,
  Users,
  History
} from "lucide-react";

import { FederalAttachments } from "./Attachments";
import { ContractHistory } from "./ContractHistory";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Recommendations } from "./Recommendations";
import { TopContractors } from "./TopContractors";
import { AwardedContracts } from "./AwardedContracts";
import Subcontractors from "./Subcontractors";

import {
  ContactDetail,
  Detail,
  ConditionSeparator,
  ContractHeader,
} from "./ContractDetails";
import { toPascalCase } from "../../utils/helpers";
import { useContext } from "react";
import { ContractContext } from "./ContractDescription";
import { Separator } from "@/components/ui/separator";
import DOMPurify from "dompurify";
import { naicsCodes } from "../search/FilterValues";


export default function FederalContract({ contract }) {
  const {
    NoticeId = null,
    Type = null,
    Department_Ind_Agency = null,
    Sub_Tier = null,
    Office = null,
    ResponseDeadLine = null,
    PostedDate = null,
    NaicsCode = null,
    FPDS_Code = null,
    PrimaryContactFullname = null,
    PrimaryContactEmail = null,
    PrimaryContactPhone = null,
    SetASide = null,
    PopStreetAddress = null,
    PopCity = null,
    PopState = null,
    PopZip = null,
    PopCountry = null,
    SecondaryContactFullname = null,
    SecondaryContactEmail = null,
    SecondaryContactPhone = null,
    Description: desc = null,
    Sol = null,
  } = contract || {};
  const { isLoading } = useContext(ContractContext);
  const sanitizedDesc = DOMPurify.sanitize(desc);
  const headerData = [
    {
      title: "Notice Type",
      icon: FileText,
      details: [Type],
    },
    {
      title: "Buyer",
      icon: Landmark,
      details: [toPascalCase(Department_Ind_Agency), toPascalCase(Sub_Tier)],
    },
    {
      title: "Office",
      icon: Building2,
      details: [toPascalCase(Office)],
    },
    {
      title: "Place of Performance",
      icon: MapPinned,
      details: [
        [PopStreetAddress, PopCity].filter(Boolean).join(", "),
        [PopState, PopZip, PopCountry].filter(Boolean).join(" "),
      ],
    },
  ];

  const timelineData = [
    {
      title: "Post Date",
      date: PostedDate,
    },
    {
      title: "Due Date",
      date: ResponseDeadLine,
    },
  ];
  const NAICSDesc = naicsCodes.find((n) => n.code === parseInt(NaicsCode))?.name
    ? ` - ${naicsCodes.find((n) => n.code === parseInt(NaicsCode))?.name}`
    : "";
  return (
    <dl>
      <div id="contract-details" className="flex flex-col gap-4 py-4 ">
        <ContractHeader headerData={headerData} timelineData={timelineData} />

        {ConditionSeparator(isLoading, NaicsCode, FPDS_Code, SetASide)}
        <Detail
          title="NAICS code"
          icon={FileChartColumn}
          details={[`${parseInt(NaicsCode)}${NAICSDesc}` || "-"]}
        />
        <Detail title="FPDS code" icon={FileChartLine} details={[FPDS_Code]} />
        <Detail title="Set aside" icon={Handshake} details={[SetASide]} />

        {ConditionSeparator(
          isLoading,
          PrimaryContactFullname,
          PrimaryContactEmail,
          PrimaryContactPhone,
          SecondaryContactFullname,
          SecondaryContactEmail,
          SecondaryContactPhone
        )}
        <ContactDetail
          title="Primary Contact"
          icon={User}
          fullname={PrimaryContactFullname}
          email={PrimaryContactEmail}
          phone={PrimaryContactPhone}
        />

        <ContactDetail
          title="Secondary Contact"
          icon={Users}
          fullname={SecondaryContactFullname}
          email={SecondaryContactEmail}
          phone={SecondaryContactPhone}
        />
        {ConditionSeparator(isLoading, desc)}
        <div id="contract-div">
          <Detail
            title={"Description"}
            icon={Text}
            details={[
              <div
                className="leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizedDesc || desc }}
              />,
            ]}
          />
        </div>
        <Separator />

        <Detail
          title={"Attachments"}
          icon={Paperclip}
          details={[
            <FederalAttachments proposalId={NoticeId} searchType="federal" />,
          ]}
        />
        
        <Separator />

        <Detail
          title="History"
          icon={History}
          details={[
            <ContractHistory solicitation_id={Sol} searchType="federal" />,
          ]}
        />
      </div>

      <Tabs
        defaultValue="similar"
        className="w-full"
        id="contract-recommendations"
      >
        <div className="min-w-10/12 mx-auto">
          <TabsList className="flex justify-center gap-12 mb-4">
            <TabsTrigger value="similar">🔍 Similar Opportunities</TabsTrigger>
            <TabsTrigger value="top-contractors">
            📊 Analytics
            </TabsTrigger>
            <TabsTrigger value="awarded-contracts">
              🏆 Awarded Contracts
            </TabsTrigger>
            <TabsTrigger value="subcontractors">💼 Subcontractors</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="similar">
          <Detail
            title={
              <span className="flex gap-1 items-center">
                <TextSearch className="text-primary/70"></TextSearch>
                Similar Active Opportunities
              </span>
            }
            details={[
              <Recommendations searchType="federal" description={desc} />,
            ]}
          />
        </TabsContent>
        <TabsContent value="top-contractors">
          <Detail
            title="🚀 Key Primes and analytics"
            details={[<TopContractors naics={parseInt(NaicsCode)} agencyName={Department_Ind_Agency}  naicsDescription={NAICSDesc}/>]}
          />
        </TabsContent>
        <TabsContent value="awarded-contracts">
          <Detail
            title="🏆 Similar Previously Awarded Contracts"
            details={[
              <AwardedContracts
                searchType="federal"
                naics={NaicsCode}
                description={desc}
              />,
            ]}
          />
        </TabsContent>
        <TabsContent value="subcontractors">
          <Detail
            title="💼 Potential Subcontractors"
            details={[
              <Subcontractors
                naics={NaicsCode}
                description={desc}
                streetAddress={PopStreetAddress}
                city={PopCity}
                state={PopState}
                zip={PopZip}
                country={PopCountry}
              />,
            ]}
          />
        </TabsContent>
      </Tabs>
    </dl>
  );
}

